import React, { useState } from 'react';
import { Cpu, Zap, Shield, Wifi, Calculator, Clock, DollarSign, Home } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
export function TechSpecsSection() {
  // Accordion State
  const [openSection, setOpenSection] = useState<'electric' | 'hydronic' | null>('electric');
  // Calculator State
  const [sizeType, setSizeType] = useState('large');
  const [customSize, setCustomSize] = useState('600');
  const [systemType, setSystemType] = useState('electric');
  const [currentMethod, setCurrentMethod] = useState('plowing');
  const [showResults, setShowResults] = useState(true);
  // Calculation Logic
  const calculateCosts = () => {
    const sqFt = sizeType === 'single' ? 200 : sizeType === 'double' ? 400 : sizeType === 'large' ? 600 : parseInt(customSize) || 0;
    // Different costs for electric vs hydronic
    let installLow, installHigh, operatingCost;
    if (systemType === 'electric') {
      installLow = sqFt * 15;
      installHigh = sqFt * 25;
      operatingCost = sqFt * 0.12;
    } else {
      // Hydronic: higher upfront, lower operating
      installLow = sqFt * 20;
      installHigh = sqFt * 30;
      operatingCost = sqFt * 0.08;
    }
    let annualSavings = 0;
    let timeSaved = 0;
    if (currentMethod === 'plowing') {
      annualSavings = 600; // Avg plowing cost
      timeSaved = 2; // Minimal time saved vs waiting for plow
    } else if (currentMethod === 'blower') {
      annualSavings = 200; // Gas/maintenance
      timeSaved = 20;
    } else {
      // DIY Shovelling
      annualSavings = 0; // No monetary cost
      timeSaved = 30;
    }
    // Property value increase based on size
    let propertyValueIncrease = 0;
    if (sizeType === 'single') {
      propertyValueIncrease = 3.0;
    } else if (sizeType === 'double') {
      propertyValueIncrease = 3.5;
    } else if (sizeType === 'large') {
      propertyValueIncrease = 4.0;
    } else {
      propertyValueIncrease = 5.0;
    }
    // Calculate dollar value based on average Ontario home price
    const avgHomePrice = 840000;
    const propertyValueDollar = avgHomePrice * (propertyValueIncrease / 100);
    // Break even (very rough estimate, mostly for fun/marketing)
    // If savings are 0, break even is infinite.
    const breakEven = annualSavings > 0 ? ((installLow + installHigh) / 2 / annualSavings).toFixed(1) : 'N/A';
    return {
      installLow,
      installHigh,
      operatingCost,
      annualSavings,
      timeSaved,
      breakEven,
      propertyValueIncrease,
      propertyValueDollar
    };
  };
  const results = calculateCosts();
  const toggleSection = (section: 'electric' | 'hydronic') => {
    setOpenSection(openSection === section ? null : section);
  };
  const specs = [{
    icon: Cpu,
    title: 'Smart Sensors',
    description: 'Advanced aerial and pavement sensors detect both moisture and temperature. The system only activates when snow or ice is present.'
  }, {
    icon: Zap,
    title: 'Energy Efficient',
    description: 'Our automated controllers modulate heat output based on ambient conditions. Typical operation costs are just pennies per square foot per storm.'
  }, {
    icon: Shield,
    title: 'Industrial Grade',
    description: 'Built to withstand decades of Canadian winters. Our systems use premium materials and proven engineering for unmatched durability and reliable performance year after year.'
  }, {
    icon: Wifi,
    title: 'WiFi Enabled Controls',
    description: 'Monitor and control your system from your smartphone. Receive alerts, check status, and view energy usage statistics from anywhere.'
  }];
  return <section className="py-16 bg-slate-900 text-white md:py-24" data-id="element-341">
      <div className="container mx-auto px-4 md:px-6" data-id="element-342">
        <div className="grid lg:grid-cols-2 gap-12 items-start" data-id="element-343">
          {/* Left Column: Specs & Systems */}
          <div data-id="element-344">
            <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary-foreground mb-6" data-id="element-345">
              <span className="flex h-2 w-2 rounded-full bg-primary mr-2" data-id="element-346"></span>
              Technical Specifications
            </div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-6" data-id="element-347">
              Engineered for Performance
            </h2>
            <p className="text-lg text-slate-300 mb-8" data-id="element-348">
              Sunflow systems are sophisticated climate management solutions
              designed for the harsh Canadian climate.
            </p>

            {/* 4 Grid Specs */}
            <div className="grid sm:grid-cols-2 gap-6 mb-12" data-id="element-349">
              {specs.map((spec, index) => <div key={index} className="flex gap-4" data-id="element-350">
                  <div className="shrink-0" data-id="element-351">
                    <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700" data-id="element-352">
                      <spec.icon className="h-5 w-5 text-primary" data-id="element-353" />
                    </div>
                  </div>
                  <div data-id="element-354">
                    <h3 className="font-bold text-white mb-1" data-id="element-355">{spec.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed" data-id="element-356">
                      {spec.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Expandable System Details */}
            <div className="space-y-4" data-id="element-357">
              {/* Electric System */}
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 overflow-hidden" data-id="element-358">
                <div className="p-4" data-id="element-359">
                  <h3 className="font-bold text-lg text-white mb-3" data-id="element-360">
                    Electric Heated Driveway Systems
                  </h3>
                  <div className="text-slate-300 space-y-3" data-id="element-361">
                    <p data-id="element-362">
                      Electric systems use embedded heating cables powered by
                      your home's electrical system. Ideal for most residential
                      driveways due to their efficiency and responsiveness.
                    </p>
                    <ul className="space-y-1 text-sm" data-id="element-363">
                      <li className="flex items-center gap-2" data-id="element-364">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-365"></span>
                        Quick installation with minimal disruption
                      </li>
                      <li className="flex items-center gap-2" data-id="element-366">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-367"></span>
                        Lower upfront cost compared to hydronic
                      </li>
                      <li className="flex items-center gap-2" data-id="element-368">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-369"></span>
                        Precise temperature control & zoning
                      </li>
                      <li className="flex items-center gap-2" data-id="element-370">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-371"></span>
                        Compatible with asphalt, concrete, and pavers
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Hydronic System */}
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 overflow-hidden" data-id="element-372">
                <div className="p-4" data-id="element-373">
                  <h3 className="font-bold text-lg text-white mb-3" data-id="element-374">
                    Hydronic Heated Driveway Systems
                  </h3>
                  <div className="text-slate-300 space-y-3" data-id="element-375">
                    <p data-id="element-376">
                      Hydronic systems circulate heated fluid (glycol) through
                      tubing beneath the surface. Best suited for large
                      commercial applications or new construction projects.
                    </p>
                    <ul className="space-y-1 text-sm" data-id="element-377">
                      <li className="flex items-center gap-2" data-id="element-378">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-379"></span>
                        Lower operating costs for very large areas
                      </li>
                      <li className="flex items-center gap-2" data-id="element-380">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-381"></span>
                        Can integrate with existing boiler systems
                      </li>
                      <li className="flex items-center gap-2" data-id="element-382">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-383"></span>
                        Extremely durable PEX tubing
                      </li>
                      <li className="flex items-center gap-2" data-id="element-384">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-385"></span>
                        Ideal for areas over 2,000 sq ft
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Calculator */}
          <div className="sticky top-24" data-id="element-386">
            <Card className="bg-slate-800 border-slate-700 shadow-2xl" data-id="element-387">
              <CardHeader className="border-b border-slate-700 pb-4" data-id="element-388">
                <div className="flex items-center gap-2 mb-1" data-id="element-389">
                  <Calculator className="h-5 w-5 text-white" data-id="element-390" />
                  <span className="text-sm font-medium text-white uppercase tracking-wider" data-id="element-391">
                    Cost Calculator
                  </span>
                </div>
                <CardTitle className="text-2xl text-white" data-id="element-392">
                  Estimate Your Investment
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6" data-id="element-393">
                {/* Inputs */}
                <div className="space-y-4" data-id="element-394">
                  <div className="space-y-2" data-id="element-395">
                    <label className="text-sm font-medium text-slate-300" data-id="element-396">
                      Driveway Size
                    </label>
                    <select className="flex h-10 w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-slate-900" value={sizeType} onChange={e => {
                    setSizeType(e.target.value);
                    setShowResults(false);
                  }} data-id="element-397">
                      <option value="single" data-id="element-398">Single Car (~200 sq ft)</option>
                      <option value="double" data-id="element-399">Double Car (~400 sq ft)</option>
                      <option value="large" data-id="element-400">Large (~600 sq ft)</option>
                      <option value="custom" data-id="element-401">Custom Size</option>
                    </select>
                  </div>

                  {sizeType === 'custom' && <div className="space-y-2 animate-in fade-in slide-in-from-top-2" data-id="element-402">
                      <label className="text-sm font-medium text-slate-300" data-id="element-403">
                        Square Footage
                      </label>
                      <div className="relative" data-id="element-404">
                        <Input type="number" value={customSize} onChange={e => {
                      setCustomSize(e.target.value);
                      setShowResults(false);
                    }} className="bg-slate-900 border-slate-600 text-white pr-12" data-id="element-405" />
                        <span className="absolute right-3 top-2.5 text-sm text-slate-500" data-id="element-406">
                          sq ft
                        </span>
                      </div>
                    </div>}

                  <div className="space-y-3" data-id="element-407">
                    <label className="text-sm font-medium text-slate-300" data-id="element-408">
                      System Type
                    </label>
                    <div className="space-y-2" data-id="element-409">
                      <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 bg-slate-900/50 cursor-pointer hover:bg-slate-700/50 transition-colors" data-id="element-410">
                        <input type="radio" name="systemType" value="electric" checked={systemType === 'electric'} onChange={() => {
                        setSystemType('electric');
                        setShowResults(false);
                      }} className="text-primary focus:ring-primary bg-slate-900 border-slate-600" data-id="element-411" />
                        <div className="flex-1" data-id="element-412">
                          <span className="text-sm font-medium text-slate-300" data-id="element-413">
                            Electric System
                          </span>
                          <p className="text-xs text-slate-500 mt-0.5" data-id="element-414">
                            Best for residential driveways
                          </p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 bg-slate-900/50 cursor-pointer hover:bg-slate-700/50 transition-colors" data-id="element-415">
                        <input type="radio" name="systemType" value="hydronic" checked={systemType === 'hydronic'} onChange={() => {
                        setSystemType('hydronic');
                        setShowResults(false);
                      }} className="text-primary focus:ring-primary bg-slate-900 border-slate-600" data-id="element-416" />
                        <div className="flex-1" data-id="element-417">
                          <span className="text-sm font-medium text-slate-300" data-id="element-418">
                            Hydronic System
                          </span>
                          <p className="text-xs text-slate-500 mt-0.5" data-id="element-419">
                            Ideal for large areas (2000+ sq ft)
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-420">
                    <label className="text-sm font-medium text-slate-300" data-id="element-421">
                      Current Snow Removal Method
                    </label>
                    <div className="space-y-2" data-id="element-422">
                      <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 bg-slate-900/50 cursor-pointer hover:bg-slate-700/50 transition-colors" data-id="element-423">
                        <input type="radio" name="method" value="plowing" checked={currentMethod === 'plowing'} onChange={() => {
                        setCurrentMethod('plowing');
                        setShowResults(false);
                      }} className="text-primary focus:ring-primary bg-slate-900 border-slate-600" data-id="element-424" />
                        <span className="text-sm text-slate-300" data-id="element-425">
                          Professional Service ($400-800/yr)
                        </span>
                      </label>
                      <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 bg-slate-900/50 cursor-pointer hover:bg-slate-700/50 transition-colors" data-id="element-426">
                        <input type="radio" name="method" value="diy" checked={currentMethod === 'diy'} onChange={() => {
                        setCurrentMethod('diy');
                        setShowResults(false);
                      }} className="text-primary focus:ring-primary bg-slate-900 border-slate-600" data-id="element-427" />
                        <span className="text-sm text-slate-300" data-id="element-428">
                          DIY Shovelling (Free)
                        </span>
                      </label>
                      <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 bg-slate-900/50 cursor-pointer hover:bg-slate-700/50 transition-colors" data-id="element-429">
                        <input type="radio" name="method" value="blower" checked={currentMethod === 'blower'} onChange={() => {
                        setCurrentMethod('blower');
                        setShowResults(false);
                      }} className="text-primary focus:ring-primary bg-slate-900 border-slate-600" data-id="element-430" />
                        <span className="text-sm text-slate-300" data-id="element-431">
                          Snow Blower (Gas/Maintenance)
                        </span>
                      </label>
                    </div>
                  </div>

                  <Button fullWidth size="lg" onClick={() => setShowResults(true)} data-id="element-432">
                    Recalculate Costs
                  </Button>
                </div>

                {/* Results */}
                {showResults && <div className="space-y-4 pt-4 border-t border-slate-700 animate-in fade-in slide-in-from-bottom-4" data-id="element-433">
                    <div className="grid grid-cols-2 gap-4" data-id="element-434">
                      <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700" data-id="element-435">
                        <p className="text-xs text-slate-400 mb-1" data-id="element-436">
                          Est. Installation
                        </p>
                        <p className="text-lg font-bold text-white" data-id="element-437">
                          ${results.installLow.toLocaleString()} - $
                          {results.installHigh.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700" data-id="element-438">
                        <p className="text-xs text-slate-400 mb-1" data-id="element-439">
                          Operating Cost
                        </p>
                        <p className="text-lg font-bold text-white" data-id="element-440">
                          ~${Math.round(results.operatingCost)}
                          <span className="text-xs font-normal text-slate-500 ml-1" data-id="element-441">
                            /yr
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="p-4 bg-primary/10 rounded-lg border border-primary/20" data-id="element-442">
                      <div className="flex items-center justify-between mb-2" data-id="element-443">
                        <div className="flex items-center gap-2" data-id="element-444">
                          <Clock className="h-4 w-4 text-green-500" data-id="element-445" />
                          <span className="text-sm font-medium text-slate-200" data-id="element-446">
                            Time Saved
                          </span>
                        </div>
                        <span className="font-bold text-white" data-id="element-447">
                          {results.timeSaved} hours/yr
                        </span>
                      </div>
                      {results.annualSavings > 0 && <>
                          <div className="flex items-center justify-between mb-2" data-id="element-448">
                            <div className="flex items-center gap-2" data-id="element-449">
                              <DollarSign className="h-4 w-4 text-green-500" data-id="element-450" />
                              <span className="text-sm font-medium text-slate-200" data-id="element-451">
                                Annual Savings
                              </span>
                            </div>
                            <span className="font-bold text-green-400" data-id="element-452">
                              ${results.annualSavings}/yr
                            </span>
                          </div>
                          <div className="flex items-center justify-between mb-2" data-id="element-453">
                            <div className="flex items-center gap-2" data-id="element-454">
                              <Clock className="h-4 w-4 text-green-500" data-id="element-455" />
                              <span className="text-sm font-medium text-slate-200" data-id="element-456">
                                Time Saved During Payback
                              </span>
                            </div>
                            <span className="font-bold text-white" data-id="element-457">
                              {Math.round((results.installLow + results.installHigh) / 2 / results.annualSavings * results.timeSaved)}{' '}
                              -{' '}
                              {Math.round((results.installLow + results.installHigh) / 2 / results.annualSavings * results.timeSaved * 1.2)}{' '}
                              hours
                            </span>
                          </div>
                          <div className="flex items-center justify-between mb-2" data-id="element-458">
                            <div className="flex items-center gap-2" data-id="element-459">
                              <DollarSign className="h-4 w-4 text-green-500" data-id="element-460" />
                              <span className="text-sm font-medium text-slate-200" data-id="element-461">
                                Payback Period
                              </span>
                            </div>
                            <span className="font-bold text-green-400" data-id="element-462">
                              {(results.installLow / results.annualSavings).toFixed(1)}{' '}
                              -{' '}
                              {(results.installHigh / results.annualSavings).toFixed(1)}{' '}
                              years
                            </span>
                          </div>
                        </>}
                      <div className="flex items-center justify-between mt-2" data-id="element-463">
                        <div className="flex items-center gap-2" data-id="element-464">
                          <Home className="h-4 w-4 text-green-500" data-id="element-465" />
                          <span className="text-sm font-medium text-slate-200" data-id="element-466">
                            Home Value Increase
                          </span>
                        </div>
                        <span className="font-bold text-white" data-id="element-467">
                          ~{results.propertyValueIncrease.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2" data-id="element-468">
                        <div className="flex items-center gap-2" data-id="element-469">
                          <DollarSign className="h-4 w-4 text-green-500" data-id="element-470" />
                          <span className="text-sm font-medium text-slate-200" data-id="element-471">
                            Added Value (Avg. ON Home)
                          </span>
                        </div>
                        <span className="font-bold text-green-400" data-id="element-472">
                          ~$
                          {Math.round(results.propertyValueDollar).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <p className="text-xs text-center text-slate-500 mt-2" data-id="element-473">
                      *Estimates only. Actual costs vary by site conditions and
                      energy rates.
                    </p>

                    <Button fullWidth variant="outline" onClick={() => {
                  document.getElementById('quote-form')?.scrollIntoView({
                    behavior: 'smooth'
                  });
                }} className="border-slate-600 text-primary hover:bg-slate-700 hover:text-primary" data-id="element-474">
                      Get Exact Quote
                    </Button>
                  </div>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}